import {
  getErrorMessage,
  getFieldErrors,
  getRequestId,
  isClientError,
  isUnauthorized,
} from "@onadssuper/api-client";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isPublicPath, markSignedOut, wasSignedIn } from "./session";

let onSessionExpired: (() => void) | undefined;

/** The router registers the redirect once it exists. */
export const setSessionExpiredHandler = (fn: () => void) => {
  onSessionExpired = fn;
};

const handleUnauthorized = (error: unknown) => {
  if (!isUnauthorized(error) || isPublicPath(window.location.pathname) || !wasSignedIn())
    return false;
  markSignedOut();
  onSessionExpired?.();
  return true;
};

export const showErrorToast = (error: unknown) => {
  const requestId = getRequestId(error);
  toast.error(
    getErrorMessage(error),
    requestId ? { description: `Reference: ${requestId}` } : undefined,
  );
};

export const createQueryClient = () => {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error) => void handleUnauthorized(error) }),
    mutationCache: new MutationCache({
      // Every change writes an activity line, so any open feed is out of date after one.
      onSuccess: () =>
        void client.invalidateQueries({
          predicate: ({ queryKey }) => Array.isArray(queryKey[0]) && queryKey[0][0] === "activity",
        }),
      onError: (error, _vars, _ctx, mutation) => {
        if (handleUnauthorized(error)) return;
        // Forms show field errors inline; everything else becomes a toast.
        if (mutation.meta?.inlineErrors && Object.keys(getFieldErrors(error)).length > 0) return;
        if (mutation.meta?.silent) return;
        showErrorToast(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (count, error) => !isClientError(error) && count < 2,
      },
      mutations: { retry: false },
    },
  });
  return client;
};

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: { inlineErrors?: boolean; silent?: boolean };
  }
}
